import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Autocomplete, MenuItem, Stack, Alert,
} from '@mui/material';
import { COMPONENT_STATUSES, COMPONENT_STATUS_LABEL } from '../../utils/constants';

// FEAT-003 ComponentAddDialog — 행 ⋮ 메뉴 "구성 SW 추가" 모달.
// 신규 component 항목 추가. SW 자동 완성 + 버전 freeSolo + 상태 + notes.
//
// Props:
//   open      : boolean
//   onClose   : 모달 닫기
//   feature   : 추가 대상 Feature (참고 표시용)
//   softwares : SW 메타 list (자동 완성 소스)
//   onSave    : (newComponent) => void
export default function ComponentAddDialog({ open, onClose, feature, softwares, onSave }) {
  const [swOption, setSwOption] = useState(null);
  const [version, setVersion] = useState('');
  const [status, setStatus] = useState('planned');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSwOption(null);
    setVersion('');
    setStatus('planned');
    setNotes('');
    setError('');
  }, [open]);

  const versionOptions = [];
  if (swOption?.versions?.latest?.tag) versionOptions.push(swOption.versions.latest.tag);
  (swOption?.versions?.planned || []).forEach((p) => { if (p.tag) versionOptions.push(p.tag); });

  const handleSave = () => {
    if (!swOption) {
      setError('소프트웨어 선택 필수');
      return;
    }
    // 중복 component 검증 — 같은 SW + 같은 version 이 이미 있으면 차단
    const dup = (feature?.components || []).some(
      (c) => c.product === swOption.id && (c.version || '') === version.trim(),
    );
    if (dup) {
      setError('같은 소프트웨어 + 버전 조합이 이미 추가됨');
      return;
    }
    onSave({
      product: swOption.id,
      version: version.trim(),
      status,
      notes: notes.trim(),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>구성 소프트웨어 추가</DialogTitle>
      <DialogContent>
        {feature && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{feature.name || feature.id}</strong> 에 새 구성 소프트웨어 추가
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            size="small"
            options={softwares}
            value={swOption}
            onChange={(_, v) => { setSwOption(v); setError(''); }}
            getOptionLabel={(o) => o ? `${o.name || o.id} (${o.id})` : ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(opts, { inputValue }) => {
              const q = (inputValue || '').toLowerCase();
              return opts.filter((o) => (`${o.name || ''} ${o.id}`).toLowerCase().includes(q));
            }}
            renderInput={(params) => (
              <TextField {...params} label="소프트웨어" placeholder="검색: 이름 또는 식별자..." required />
            )}
          />

          <Autocomplete
            freeSolo
            size="small"
            options={versionOptions}
            value={version}
            onChange={(_, v) => setVersion(typeof v === 'string' ? v : '')}
            onInputChange={(_, v, reason) => { if (reason === 'input') setVersion(v); }}
            renderInput={(params) => (
              <TextField {...params} label="버전" placeholder="예: v4.0.0 또는 TBD" />
            )}
            disabled={!swOption}
          />

          <TextField
            select
            size="small"
            label="상태"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {COMPONENT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {COMPONENT_STATUS_LABEL[s]} ({s})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="메모 (선택)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            placeholder="자유 메모"
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={handleSave} disabled={!swOption}>
          추가 → buffer 에 누적
        </Button>
      </DialogActions>
    </Dialog>
  );
}
